import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  QuoteDocumentConflictBodySchema,
  QuoteDocumentReadSchema,
  type QuoteDocumentConflictCode,
  type UpdateQuoteDocumentParams,
} from "@forgepoint/shared";

/** A quote's stored document plus its optimistic-concurrency version. */
export interface StoredQuoteDocument {
  /** The stored document HTML. */
  document: string;
  /** The optimistic-concurrency version the HTML was read at. */
  version: number;
  /** The template the document was last seeded from, or null when detached. */
  baseTemplateId: string | null;
}

/** Per-quote loading and error state for the document GET/PUT calls. */
export interface QuoteDocumentLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  getError: string | null;
  saveError: string | null;
}

/** Result of a document save: the updated document, or an error. */
export interface SaveDocumentResult {
  /** The saved document + new version on success, else null. */
  document: StoredQuoteDocument | null;
  /** The server error message on failure, else null. */
  error: string | null;
  /** The HTTP status of the save response, so callers can branch on 409/400. */
  status: number;
  /**
   * On a 409, distinguishes a recoverable edit conflict from a permanent
   * finalized-quote lock; null otherwise. Lets the autosave hook lock the
   * editor instead of looping the reload-or-keep dialog on a finalized quote.
   */
  conflictCode: QuoteDocumentConflictCode | null;
}

interface QuoteDocumentsStore {
  /** The stored document per quote id. */
  documents: Map<string, StoredQuoteDocument>;
  /** Per-quote loading/error state, keyed by quote id. */
  documentStates: Map<string, QuoteDocumentLoadingState>;
  /** Fetches and stores a quote's document, returning it or null on failure. */
  fetchDocument: (quoteId: string) => Promise<StoredQuoteDocument | null>;
  /** Saves a quote's document under its OCC version, returning the result. */
  saveDocument: (
    quoteId: string,
    params: UpdateQuoteDocumentParams,
  ) => Promise<SaveDocumentResult>;
}

const getInitialLoadingState = (): QuoteDocumentLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  getError: null,
  saveError: null,
});

/**
 * Zustand store for per-quote stored documents. Documents are keyed by quote
 * id in a map, with a parallel per-id loading-state map, mirroring the quotes
 * store's entity pattern. The editor seeds from `fetchDocument` and persists
 * through `saveDocument`, which returns the response status so the autosave
 * hook can branch on 409 conflicts and 400 validation rejections.
 */
export const useQuoteDocumentsStore = create<QuoteDocumentsStore>(
  (set, get) => ({
    documents: new Map(),
    documentStates: new Map(),

    fetchDocument: async (
      quoteId: string,
    ): Promise<StoredQuoteDocument | null> => {
      set((state) => ({
        documentStates: withLoadingState(state.documentStates, quoteId, {
          isLoadingGet: true,
          getError: null,
        }),
      }));

      try {
        const response = await fetchApi(`quotes/${quoteId}/document`, "GET");
        if (response.ok && response.data) {
          const document: StoredQuoteDocument = QuoteDocumentReadSchema.parse(
            response.data,
          );
          set((state) => {
            const documents = new Map(state.documents);
            if ((documents.get(quoteId)?.version ?? -1) <= document.version) {
              documents.set(quoteId, document);
            }
            return {
              documents,
              documentStates: withLoadingState(state.documentStates, quoteId, {
                isLoadingGet: false,
                getError: null,
              }),
            };
          });
          return get().documents.get(quoteId) ?? document;
        }
        set((state) => ({
          documentStates: withLoadingState(state.documentStates, quoteId, {
            isLoadingGet: false,
            getError: "Failed to load quote document",
          }),
        }));
        return null;
      } catch (error) {
        captureException(error as Error);
        set((state) => ({
          documentStates: withLoadingState(state.documentStates, quoteId, {
            isLoadingGet: false,
            getError: "Failed to load quote document",
          }),
        }));
        return null;
      }
    },

    saveDocument: async (
      quoteId: string,
      params: UpdateQuoteDocumentParams,
    ): Promise<SaveDocumentResult> => {
      set((state) => ({
        documentStates: withLoadingState(state.documentStates, quoteId, {
          isLoadingSave: true,
          saveError: null,
        }),
      }));

      try {
        const response = await fetchApi(`quotes/${quoteId}/document`, "PUT", {
          body: params,
        });
        if (response.ok && response.data) {
          const document: StoredQuoteDocument = QuoteDocumentReadSchema.parse(
            response.data,
          );
          set((state) => {
            const documents = new Map(state.documents);
            if ((documents.get(quoteId)?.version ?? -1) <= document.version) {
              documents.set(quoteId, document);
            }
            return {
              documents,
              documentStates: withLoadingState(state.documentStates, quoteId, {
                isLoadingSave: false,
                saveError: null,
              }),
            };
          });
          return {
            document,
            error: null,
            status: response.status,
            conflictCode: null,
          };
        }
        const error = response.error ?? "Failed to save quote document";
        set((state) => ({
          documentStates: withLoadingState(state.documentStates, quoteId, {
            isLoadingSave: false,
            saveError: error,
          }),
        }));
        return {
          document: null,
          error,
          status: response.status,
          conflictCode: parseConflictCode(response.data),
        };
      } catch (error) {
        captureException(error as Error);
        set((state) => ({
          documentStates: withLoadingState(state.documentStates, quoteId, {
            isLoadingSave: false,
            saveError: "Failed to save quote document",
          }),
        }));
        return {
          document: null,
          error: "Failed to save quote document",
          status: 0,
          conflictCode: null,
        };
      }
    },
  }),
);

/**
 * Extracts the document-write 409 discriminator from a failed response body,
 * distinguishing an edit conflict from a finalized-quote lock. Returns null
 * when the body is not a recognized conflict payload (e.g. a 400 or 500).
 * @param data - The parsed response body from a failed save
 * @returns The conflict discriminator code, or null
 */
function parseConflictCode(data: unknown): QuoteDocumentConflictCode | null {
  const parsed = QuoteDocumentConflictBodySchema.safeParse(data);
  return parsed.success ? parsed.data.code : null;
}

/**
 * Returns a new loading-state map with the given quote's state patched,
 * preserving the store's immutable-map update convention.
 * @param states - The current per-quote loading-state map
 * @param quoteId - The quote id whose state is patched
 * @param patch - The partial loading state to merge in
 * @returns A new map with the patched entry
 */
function withLoadingState(
  states: Map<string, QuoteDocumentLoadingState>,
  quoteId: string,
  patch: Partial<QuoteDocumentLoadingState>,
): Map<string, QuoteDocumentLoadingState> {
  const next = new Map(states);
  const current = next.get(quoteId) ?? getInitialLoadingState();
  next.set(quoteId, { ...current, ...patch });
  return next;
}
